import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionPopup } from 'components/Popup/ActionPopup'
import { EVENT_NAMES, SCREENS } from 'services/Analytics/Analytics'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import type { CartItem } from 'stores/cartStore/CartItem'
import { useTilesStore } from 'stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { getCartUpsellTilesAnalyticsProps } from 'features/cartUpsells/cartUpsellTiles'
import { getRemoveItemConfirmationMessage } from '../removeItemConfirmationMessage'
import { useBagAnalytics } from './useBagAnalytics'

type UseRemoveItemPopupProps = {
  onCartEmptied: () => void
}

// Confirmation popup for removing an item from the bag.
export function useRemoveItemPopup({ onCartEmptied }: UseRemoveItemPopupProps) {
  const { t } = useTranslation()
  const cartStore = useCartStore()
  const tilesStore = useTilesStore()
  const { trackBagItemEvent } = useBagAnalytics()
  const [itemToRemove, setItemToRemove] = useState<CartItem | null>(null)
  const lastItemRef = useRef<CartItem | null>(null)

  const requestRemoveConfirmation = (item: CartItem) => {
    lastItemRef.current = item
    setItemToRemove(item)
    trackBagItemEvent(EVENT_NAMES.CART_REMOVE_ITEM_TAPPED, item, {
      Source: SCREENS.CART_DRAWER,
    })
  }

  const onRemove = () => {
    if (!itemToRemove) return
    const removedCartUpsellTilesCount = readOutsideReaction(() =>
      itemToRemove.productType === PRODUCT_TYPES.CLASSIC
        ? getCartUpsellTilesAnalyticsProps(tilesStore.tiles)[
            'Cart Upsell Tiles Count'
          ]
        : 0
    )
    trackBagItemEvent(EVENT_NAMES.CART_REMOVE_ITEM_CONFIRMED, itemToRemove, {
      Source: SCREENS.CART_DRAWER,
      'Cart Upsell Tiles Count': removedCartUpsellTilesCount,
    })
    cartStore.removeItem(itemToRemove.id)
    setItemToRemove(null)

    if (readOutsideReaction(() => cartStore.cartItems.length) === 0) {
      onCartEmptied()
    }
  }

  const onCancel = () => {
    if (!itemToRemove) return
    trackBagItemEvent(EVENT_NAMES.CART_REMOVE_ITEM_CANCELLED, itemToRemove, {
      Source: SCREENS.CART_DRAWER,
    })
    setItemToRemove(null)
  }

  const displayedItem = itemToRemove ?? lastItemRef.current

  const popup = displayedItem && (
    <ActionPopup
      popupName="Remove Item"
      open={!!itemToRemove}
      mainText={getRemoveItemConfirmationMessage(displayedItem.productType, t)}
      approveText={t('order.remove_item_popup.delete')}
      onApproveTap={onRemove}
      dismissText={t('general.cancel')}
      onDismissTap={onCancel}
      onClose={onCancel}
      analyticsProps={{ Source: SCREENS.CART_DRAWER }}
    />
  )

  return { popup, requestRemoveConfirmation }
}
